import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import { ISSUE_SEVERITY } from "contracts/enums/core";
import { CORE_WEB_VITALS_CONFIDENCE_LEVEL } from "contracts/enums/web-vitals";
import type { PriorityFlowTier } from "../../enums/scan";
import { JOURNEY_FRICTION_KIND, PRIORITY_FLOW_TIER } from "../../enums/scan";
import { issueLinkMatchesPage } from "./issuePageResolution";
import { canonicalIssueProblemKey } from "./issuePresentationPriority";
import { journeyFrictionNextStep } from "./journeyFriction";
import type {
  CandidateJourney,
  JourneyFriction,
  JourneyPage,
  PriorityFlowConcern,
} from "./journeyTypes";
import {
  compareCandidates,
  compareConcerns,
  compareIssues,
} from "./priorityFlowOrdering";

export type ConcernCandidate = {
  key: string;
  journey: CandidateJourney;
  friction: JourneyFriction;
};

export function buildPriorityFlowConcerns(
  data: OverviewResponse,
  paths: CandidateJourney[],
): PriorityFlowConcern[] {
  const groups = new Map<string, ConcernCandidate[]>();
  for (const journey of paths) {
    for (const friction of journey.concernCandidates) {
      const key = canonicalConcernKey(friction);
      groups.set(key, [...(groups.get(key) ?? []), { key, journey, friction }]);
    }
  }
  return [...groups.values()]
    .map((rows) => mergeConcern(data, rows))
    .sort(compareConcerns);
}

export function canonicalConcernKey(friction: JourneyFriction): string {
  if (friction.issue) {
    const problemKey = canonicalIssueProblemKey(friction.issue);
    return `issue:${problemKey || friction.issue.id}`;
  }
  if (friction.kind === JOURNEY_FRICTION_KIND.PARTIAL_COVERAGE) {
    return "structural:partial-coverage";
  }
  if (friction.kind === JOURNEY_FRICTION_KIND.NO_ONWARD_LINK) {
    return `structural:no-onward-link:${friction.page?.id ?? "unknown"}`;
  }
  return `structural:${friction.kind}:${friction.key}`;
}

export function priorityTierForConcerns(
  concerns: PriorityFlowConcern[],
  priorityPageCount: number,
): PriorityFlowTier {
  if (
    concerns.some(
      ({ friction }) =>
        friction.kind === JOURNEY_FRICTION_KIND.BROKEN_LINK ||
        friction.issue?.severity === ISSUE_SEVERITY.CRITICAL,
    )
  ) {
    return PRIORITY_FLOW_TIER.CRITICAL;
  }
  if (
    concerns.some(
      ({ friction }) =>
        friction.issue?.severity === ISSUE_SEVERITY.HIGH ||
        friction.kind === JOURNEY_FRICTION_KIND.NO_ONWARD_LINK,
    ) ||
    priorityPageCount > 1
  ) {
    return PRIORITY_FLOW_TIER.HIGH;
  }
  return concerns.length > 0
    ? PRIORITY_FLOW_TIER.REVIEW
    : PRIORITY_FLOW_TIER.CONTEXT;
}

export function priorityReasonForConcerns(
  concerns: PriorityFlowConcern[],
  priorityPageCount: number,
): string {
  if (
    concerns.some(
      ({ friction }) => friction.kind === JOURNEY_FRICTION_KIND.BROKEN_LINK,
    )
  ) {
    return "A recorded broken link affects this current path.";
  }
  if (
    concerns.some(
      ({ friction }) => friction.issue?.severity === ISSUE_SEVERITY.CRITICAL,
    )
  ) {
    return "A critical onward-navigation issue affects this current path.";
  }
  if (
    concerns.some(
      ({ friction }) => friction.issue?.severity === ISSUE_SEVERITY.HIGH,
    )
  ) {
    return "A high-priority onward-navigation issue affects this current path.";
  }
  if (
    concerns.some(
      ({ friction }) => friction.kind === JOURNEY_FRICTION_KIND.NO_ONWARD_LINK,
    )
  ) {
    return "A meaningful route has no recorded onward link.";
  }
  if (priorityPageCount > 1) {
    return `${priorityPageCount} priority-linked pages need structural review.`;
  }
  if (concerns.some(({ friction }) => friction.issue)) {
    return "A supported navigation or next-step issue needs review.";
  }
  if (
    concerns.some(
      ({ friction }) =>
        friction.kind === JOURNEY_FRICTION_KIND.PARTIAL_COVERAGE,
    )
  ) {
    return "Assessment coverage is incomplete across this current path.";
  }
  return "No priority structural concern was identified.";
}

export function priorityPageCount(concerns: PriorityFlowConcern[]): number {
  return new Set(
    concerns.flatMap(({ friction, affectedPages }) =>
      friction.issue?.severity === ISSUE_SEVERITY.CRITICAL ||
      friction.issue?.severity === CORE_WEB_VITALS_CONFIDENCE_LEVEL.HIGH
        ? affectedPages.map((page) => page.id)
        : [],
    ),
  ).size;
}

export function criticalHighConcernCount(
  concerns: PriorityFlowConcern[],
): number {
  return concerns.filter(
    ({ friction }) =>
      friction.kind === JOURNEY_FRICTION_KIND.BROKEN_LINK ||
      friction.issue?.severity === ISSUE_SEVERITY.CRITICAL ||
      friction.issue?.severity === CORE_WEB_VITALS_CONFIDENCE_LEVEL.HIGH,
  ).length;
}

export function brokenOrDeadEndCount(concerns: PriorityFlowConcern[]): number {
  return concerns.filter(
    ({ friction }) =>
      friction.kind === JOURNEY_FRICTION_KIND.BROKEN_LINK ||
      friction.kind === JOURNEY_FRICTION_KIND.NO_ONWARD_LINK,
  ).length;
}

function mergeConcern(
  data: OverviewResponse,
  candidates: ConcernCandidate[],
): PriorityFlowConcern {
  const sorted = [...candidates].sort(compareCandidates);
  const representative = sorted[0] as ConcernCandidate;
  const issueRecords = uniqueIssues(
    sorted.flatMap(({ friction }) => (friction.issue ? [friction.issue] : [])),
  );
  return {
    ...representative,
    nextStep: journeyFrictionNextStep(data, representative.friction),
    issueRecords,
    affectedPages: affectedPagesForConcern(data, sorted, issueRecords),
  };
}

function affectedPagesForConcern(
  data: OverviewResponse,
  candidates: ConcernCandidate[],
  issueRecords: Issue[],
): JourneyPage[] {
  const pages = candidates.flatMap(({ friction }) =>
    friction.page ? [friction.page] : [],
  );
  for (const issue of issueRecords) {
    pages.push(
      ...data.pages.filter((page) =>
        issue.pages.some((link) => issueLinkMatchesPage(link, page)),
      ),
    );
  }
  return [
    ...new Map(pages.map((page) => [page.id, page] as const)).values(),
  ].sort((left, right) => left.path.localeCompare(right.path));
}

function uniqueIssues(issues: Issue[]): Issue[] {
  return [
    ...new Map(issues.map((issue) => [issue.id, issue] as const)).values(),
  ].sort(compareIssues);
}
